import * as React from 'react';
import { useEffect, useState } from 'react';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Flex, Text } from '@opendoor/bricks/core';
import Input, { InputProps } from '@opendoor/bricks/core/Input';
import { formatCurrency } from '@opendoor/shared-fe/build/formatUtils';

interface INumberRange {
  min?: string;
  max?: string;
}

type INumberRangeInputProps = {
  name: string;
  value: INumberRange | undefined;
  onChange: (
    key: string,
    value: any,
    hasError?: boolean | undefined,
    errorMessage?: string | undefined,
  ) => void;
  maxPlaceholder?: string | undefined;
  minPlaceholder?: string | undefined;
} & Pick<InputProps, 'max' | 'min' | 'required' | 'unboundedWidth' | 'autoFocus'>;

const NumberRangeInput: React.FC<INumberRangeInputProps> = ({
  name,
  value,
  onChange,
  max,
  min,
  required,
  unboundedWidth,
  autoFocus,
  minPlaceholder,
  maxPlaceholder,
}) => {
  const [minValue, setMinValue] = useState<string | undefined>(value?.min);
  const [maxValue, setMaxValue] = useState<string | undefined>(value?.max);

  const { error } = useValidateNumberRange({
    minValue,
    maxValue,
    minLimit: min,
    maxLimit: max,
  });

  useEffect(() => {
    onChange(
      name,
      {
        min: minValue,
        max: maxValue,
      },
      !!error,
      error,
    );
    // eslint-disable-next-line
  }, [error]);

  return (
    <Flex flexDirection="column" alignItems={['flex-start', 'center']}>
      <Flex>
        <Flex flexDirection="column" textAlign="left">
          <Text> Minimum </Text>
          <Input
            analyticsName={`reception-fe-number-range-input-${name}-lower`}
            id={`${name}-lower`}
            type="currency"
            onChange={(_event, newValue) => setMinValue(newValue)}
            value={value?.min}
            name={name}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            unboundedWidth={unboundedWidth}
            placeholder={minPlaceholder}
            aria-label={minPlaceholder}
            required={required}
          />
        </Flex>
        <Text mx="24px" mt="40px" fontWeight="bold">
          to
        </Text>
        <Flex flexDirection="column" textAlign="left">
          <Text> Maximum </Text>
          <Input
            analyticsName={`reception-fe-number-range-input-${name}-upper`}
            id={`${name}-upper`}
            type="currency"
            onChange={(_event, newValue) => setMaxValue(newValue)}
            value={value?.max}
            name={name}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            unboundedWidth={unboundedWidth}
            placeholder={maxPlaceholder}
            aria-label={maxPlaceholder}
            required={required}
          />
        </Flex>
      </Flex>
      {error && (
        <Flex mt="6">
          <ErrorPrompt message={error} />
        </Flex>
      )}
    </Flex>
  );
};

export const MIN_GREATER_THAN_MAX_ERROR = 'Minimum must be less than maximum';
export const INVALID_NUMBER_ERROR = 'contains non-numeral values';

export interface IValidateNumberRangeProps {
  minValue: string | undefined;
  maxValue: string | undefined;
  minLimit?: number | undefined;
  maxLimit?: number | undefined;
}

export const useValidateNumberRange = ({
  minValue, // the inputted value for the maximum range
  maxValue, // the inputted value for the minimum range
  minLimit, // we do not allow inputted numbers to be less than this value
  maxLimit, // we do not allow inputted numbers to be greater than this value
}: IValidateNumberRangeProps) => {
  const [error, setError] = useState<string | undefined>('');

  useEffect(() => {
    // check that values only contain number characters
    if (minValue && !isValidNumber(minValue)) {
      setError(`Minimum ${INVALID_NUMBER_ERROR}`);
      return;
    }
    if (maxValue && !isValidNumber(maxValue)) {
      setError(`Maximum ${INVALID_NUMBER_ERROR}`);
      return;
    }
    const minNum = minValue ? parseInt(minValue, 10) : undefined;
    const maxNum = maxValue ? parseInt(maxValue, 10) : undefined;
    if (minNum !== undefined && maxNum !== undefined && minNum > maxNum) {
      // check that min is always less than max
      setError(MIN_GREATER_THAN_MAX_ERROR);
      return;
    }
    if (minLimit) {
      // check that values are not less than minLimit
      if (minNum !== undefined && minNum < minLimit) {
        setError(`Can't be less than ${formatCurrency(minLimit)}`);
        return;
      }
      if (maxNum !== undefined && maxNum < minLimit) {
        setError(`Can't be less than ${formatCurrency(minLimit)}`);
        return;
      }
    }
    if (maxLimit) {
      // check that values are not greater than maxLimit
      if (minNum !== undefined && minNum > maxLimit) {
        setError(`Can't be greater than ${formatCurrency(maxLimit)}`);
        return;
      }
      if (maxNum !== undefined && maxNum > maxLimit) {
        setError(`Can't be greater than ${formatCurrency(maxLimit)}`);
        return;
      }
    }
    setError(undefined);

    // eslint-disable-next-line
  }, [minValue, maxValue]);

  return { error };
};

const isValidNumber = (value: string): boolean => {
  return /^\d+$/.test(value);
};

export default NumberRangeInput;
